import { useEffect, useMemo, useRef, useState } from "react";
import { Bell, Dot, Ellipsis, X } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import PurchaserClient from "../../clients/PurchaserClient";
import { type Notification } from "../../clients/Purchaser/Notifications";
import SlideoutPanel from "../Common/SlideoutPanel";
import { Button, Dropdown, Spinner, Text, Toggle } from "../DesignSystem";
import MagicToast from "../DesignSystem/MagicToast";
import NotificationItem from "./NotificationItem";

type NotificationsPanelProps = {};

const PAGE_SIZE = 20;
const OVERSCAN_SIZE = 5;

export default function NotificationsPanel({}: NotificationsPanelProps) {
    const [open, setOpen] = useState(false);
    const [hideAllRead, setHideAllRead] = useState(false);
    const [hideNewNotifications, setHideNewNotifications] = useState(true);

    const { data, isLoading, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
        queryKey: ["notifications"],
        queryFn: ({ pageParam = 0 }) =>
            PurchaserClient.getNotifications({
                cursor: pageParam === 0 ? undefined : pageParam,
                pageSize: PAGE_SIZE,
            }),
        getNextPageParam: lastPage => {
            // If the last page has fewer items than the limit, we've reached the end
            if (lastPage.length < PAGE_SIZE) {
                return undefined;
            }
            // Since it is cursor pagination, we just return the last id on
            // the last page
            return lastPage[lastPage.length - 1].id;
        },
        initialPageParam: 0,
        placeholderData: keepPreviousData,
        refetchInterval: 10000,
    });

    const notifications = useMemo(() => {
        if (!data) {
            return [];
        }
        return data.pages.flat();
    }, [data]);

    const unreadCount = useMemo(() => {
        if (isLoading || !notifications.length) {
            return 0;
        }

        const unreadNotifications = notifications.filter(notification => !notification.isRead);

        return unreadNotifications.length || 0;
    }, [isLoading, notifications]);

    const onClose = () => {
        setOpen(false);
    };

    const markAsRead = async (id: number) => {
        try {
            await PurchaserClient.markAsRead(id);
            refetch();
            MagicToast.show("Marked notification as read", {
                position: "bottom",
                duration: 5000,
            });
        } catch (err) {
            console.error(err);
            MagicToast.show("Error marking notification as read", {
                position: "bottom",
                duration: 5000,
            });
        }
    };

    const markAsUnread = async (id: number) => {
        try {
            await PurchaserClient.markAsUnread(id);
            refetch();
            MagicToast.show("Marked notification as unread", {
                position: "bottom",
                duration: 5000,
            });
        } catch (err) {
            console.error(err);
            MagicToast.show("Error marking notification as unread", {
                position: "bottom",
                duration: 5000,
            });
        }
    };

    const markAllAsRead = async () => {
        const ids = notifications.filter(n => !n.isRead).map(n => n.id);
        if (!ids?.length) {
            MagicToast.show("No notifications to mark as read", {
                position: "bottom",
                duration: 5000,
            });
            return;
        }

        try {
            await PurchaserClient.markAllAsRead(ids);
            refetch();
            MagicToast.show("Marked all notifications as read", {
                position: "bottom",
                duration: 5000,
            });
        } catch (err) {
            console.error(err);
            MagicToast.show("Error marking all notifications as read", {
                position: "bottom",
                duration: 5000,
            });
        }
    };

    const deleteNotification = async (id: number) => {
        try {
            await PurchaserClient.deleteNotification(id);
            refetch();
            MagicToast.show("Deleted notification", {
                position: "bottom",
                duration: 5000,
            });
        } catch (err) {
            console.error(err);
            MagicToast.show("Error deleting notification", {
                position: "bottom",
                duration: 5000,
            });
        }
    };

    const notificationsToDisplay = useMemo(() => {
        if (isLoading || !notifications) {
            return [];
        }

        if (hideAllRead) {
            return notifications.filter(n => !n.isRead);
        }

        return notifications;
    }, [hideAllRead, isLoading, notifications]);

    const newNotifications = useMemo(() => {
        if (isLoading || !notifications) {
            return [];
        }

        const filteredNotifications = notifications.filter(n => new Date(n.createdAt).valueOf() > Date.now() - 10000);

        if (filteredNotifications.length > 0) {
            setHideNewNotifications(false);
        }

        return filteredNotifications;
    }, [notifications]);

    useEffect(() => {
        let toastTimeout: NodeJS.Timeout;
        if (newNotifications.length > 0) {
            toastTimeout = setTimeout(() => setHideNewNotifications(true), 4000);
        }
        return () => clearTimeout(toastTimeout);
    }, [newNotifications]);

    return (
        <div className="mr-2">
            <Button
                color="clear"
                className="p-2 hover:bg-stone-200 active:bg-stone-300"
                icon={
                    unreadCount > 0 ? (
                        <div className="relative">
                            <Dot className="stroke-bronze-600 bg-bronze-600 absolute top-1 right-1 h-2 w-2 shrink-0 rounded-2xl outline outline-white" />
                            <div className="rounded-2xl p-1.5">
                                <Bell className="stroke-bronze-500 fill-bronze-500 h-4 w-4 shrink-0" />
                            </div>
                        </div>
                    ) : (
                        <Bell className="h-4 w-4 shrink-0 stroke-stone-600" />
                    )
                }
                onClick={() => setOpen(true)}
            />
            <SlideoutPanel isOpen={open} shouldClose={onClose} className="overflow-hidden overscroll-contain px-4 py-3">
                <div className="flex h-8.5 flex-row items-center justify-between">
                    <div className="flex flex-row items-center gap-1.5">
                        <Text variant="body500">Notifications</Text>
                        <Text variant="smallBody600" className="bg-bronze-600 min-w-4.5 rounded-md px-0.5 text-center text-white">
                            {unreadCount}
                        </Text>
                    </div>
                    <div className="flex flex-row items-center gap-2">
                        <Dropdown
                            attachToSide="right"
                            itemWrapperClassName="p-3 flex flex-col gap-2"
                            buttonOverride={<Button as="div" color="clear" className="h-8 py-4" icon={<Ellipsis className="stroke-stone-400" />} />}
                        >
                            <Button text="Mark All as Read" color="clear" className="w-full py-1 font-medium" onClick={markAllAsRead} />
                            <Button
                                text="Manage Notification Preferences"
                                color="clear"
                                className="w-full py-1 font-medium"
                                linkClassName="w-full"
                                href="/settings/user/notifications"
                                onClick={onClose}
                            />
                            <div className="my-1 h-px w-full bg-stone-200" />
                            <div className="flex items-center justify-between px-2 py-1">
                                <Text variant="body500">Hide All Read</Text>
                                <Toggle checked={hideAllRead} onChange={() => setHideAllRead(!hideAllRead)} />
                            </div>
                        </Dropdown>
                        <Button color="clear" className="p-2" icon={<X className="h-4 w-4 shrink-0 stroke-stone-600" onClick={onClose} />} />
                    </div>
                </div>
                {isLoading && <Spinner />}
                {!isLoading && notificationsToDisplay.length === 0 && <div>You are all caught up on your notifications!</div>}
                {!isLoading && notificationsToDisplay.length > 0 && (
                    <NotificationItems
                        notifications={notificationsToDisplay}
                        markAsRead={markAsRead}
                        markAsUnread={markAsUnread}
                        deleteNotification={deleteNotification}
                        fetchNextPage={fetchNextPage}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                    />
                )}
            </SlideoutPanel>

            {!isLoading && !hideNewNotifications && newNotifications.length > 0 && (
                <div className="fixed top-12 right-12 z-50 w-80 rounded-2xl bg-white py-2 shadow">
                    {newNotifications.map(notification => (
                        <NotificationItem
                            key={notification.id}
                            notification={notification}
                            markAsRead={markAsRead}
                            markAsUnread={markAsUnread}
                            deleteNotification={deleteNotification}
                            condensed
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

type NotificationItemsProps = {
    notifications: Notification[];
    markAsRead: (id: number) => void;
    markAsUnread: (id: number) => void;
    deleteNotification: (id: number) => void;
    fetchNextPage: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
};

function NotificationItems({
    notifications,
    markAsRead,
    markAsUnread,
    deleteNotification,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
}: NotificationItemsProps) {
    const parentRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: notifications.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 80, // Approximate height of each item
        overscan: OVERSCAN_SIZE, // Render 5 extra items above and below viewport,
    });

    const virtualItems = virtualizer.getVirtualItems();

    // Load more when scrolling near the bottom
    useEffect(() => {
        const [lastItem] = [...virtualItems].reverse();

        if (!lastItem) return;

        // If we're within overscan items of the end and there's more data to load
        if (lastItem.index >= notifications.length - OVERSCAN_SIZE && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, fetchNextPage, notifications, isFetchingNextPage, virtualItems]);

    return (
        <div ref={parentRef} className="relative h-[calc(100%-34px)] overflow-y-auto">
            {virtualItems.map(virtualItem => {
                const isLast = virtualItem.index === notifications.length - 1;

                return (
                    <div
                        key={virtualItem.key}
                        data-index={virtualItem.index}
                        ref={virtualizer.measureElement}
                        className={twMerge("absolute top-0 left-0 flex w-full items-center", isLast && "pb-3")}
                        style={{
                            transform: `translateY(${virtualItem.start}px)`,
                        }}
                    >
                        <NotificationItem
                            key={virtualItem.key}
                            notification={notifications[virtualItem.index]}
                            markAsRead={markAsRead}
                            markAsUnread={markAsUnread}
                            deleteNotification={deleteNotification}
                        />
                    </div>
                );
            })}
            {isFetchingNextPage && (
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: `translateY(${virtualizer.getTotalSize()}px)`,
                    }}
                    className="p-2 text-center text-sm text-gray-500"
                >
                    Loading more...
                </div>
            )}
        </div>
    );
}
